import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserModels } from '@/hooks/useModels';
import { useQuery } from '@tanstack/react-query';
import { getProfileByUsername } from '@/services/profiles';
import { ModelGrid } from '@/components/model/ModelCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Plus, Settings, Globe, Lock, Loader2 } from 'lucide-react';
import type { ModelCardData } from '@/types/models';

export function UserProfile() {
  const { username } = useParams<{ username: string }>();
  const { profile: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('public');

  // Check if viewing own profile
  const isOwnProfile = currentUser?.username === username;

  // Fetch user profile
  const {
    data: user,
    isLoading: isLoadingProfile,
    isError: isProfileError,
  } = useQuery({
    queryKey: ['profile', username],
    queryFn: () => (username ? getProfileByUsername(username) : null),
    enabled: !!username,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch user's models
  const { data: models, isLoading: isLoadingModels } = useUserModels(
    username || null,
    {
      includePrivate: isOwnProfile,
    },
  );

  // Transform and filter models
  const modelCards: ModelCardData[] = useMemo(() => {
    if (!models || !user) return [];

    return models.map((model) => ({
      id: model.id,
      slug: model.slug,
      title: model.title,
      description: model.description,
      thumbnail_url: model.thumbnail_url,
      is_public: model.is_public,
      view_count: model.view_count,
      fork_count: model.fork_count || 0,
      created_at: model.created_at,
      updated_at: model.updated_at,
      author: {
        username: user.username,
        avatar_url: user.avatar_url,
      },
    }));
  }, [models, user]);

  const publicModels = useMemo(
    () => modelCards.filter((m) => m.is_public),
    [modelCards],
  );

  const privateModels = useMemo(
    () => modelCards.filter((m) => !m.is_public),
    [modelCards],
  );

  const formatJoinDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  // Loading state
  if (isLoadingProfile) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // User not found
  if (isProfileError || !user) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-foreground">
            User Not Found
          </h1>
          <p className="mt-2 text-muted-foreground">
            The user @{username} doesn't exist.
          </p>
          <Link to="/explore">
            <Button className="mt-4">Explore Models</Button>
          </Link>
        </div>
      </div>
    );
  }

  const initials = user.display_name
    ? user.display_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user.username.slice(0, 2).toUpperCase();

  return (
    <div className="flex h-full flex-col">
      {/* Profile Header */}
      <div className="border-b border-border px-8 py-8">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <Avatar className="h-20 w-20 border-2 border-border">
            <AvatarImage
              src={user.avatar_url || undefined}
              alt={user.display_name || user.username}
            />
            <AvatarFallback className="bg-muted text-xl text-muted-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>

          {/* User Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-foreground">
                {user.display_name || user.username}
              </h1>
              {isOwnProfile && (
                <Link to="/settings">
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <Settings className="h-3.5 w-3.5" />
                    Edit Profile
                  </Button>
                </Link>
              )}
            </div>

            <p className="mt-1 text-sm text-muted-foreground">
              @{user.username}
            </p>

            {user.bio && (
              <p className="mt-3 max-w-2xl text-sm text-foreground">
                {user.bio}
              </p>
            )}

            {/* Meta info */}
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                Joined {formatJoinDate(user.created_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Globe className="h-4 w-4" />
                {publicModels.length} public model
                {publicModels.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Create Model Button (own profile) */}
          {isOwnProfile && (
            <Link to="/new">
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                New Model
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Tabs and Content */}
      <div className="flex-1 overflow-hidden">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex h-full flex-col"
        >
          <div className="border-b border-border px-8">
            <TabsList className="h-11 bg-transparent p-0">
              <TabsTrigger
                value="public"
                className="relative h-full rounded-none border-b-2 border-transparent px-4 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground"
              >
                <Globe className="mr-2 h-4 w-4" />
                Public ({publicModels.length})
              </TabsTrigger>
              {isOwnProfile && (
                <TabsTrigger
                  value="private"
                  className="relative h-full rounded-none border-b-2 border-transparent px-4 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground"
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Private ({privateModels.length})
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto p-8">
            {isLoadingModels ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <TabsContent value="public" className="mt-0 h-full">
                  <ModelGrid
                    models={publicModels}
                    showAuthor={false}
                    emptyMessage={
                      isOwnProfile
                        ? "You haven't created any public models yet."
                        : `@${username} hasn't created any public models yet.`
                    }
                  />
                </TabsContent>

                {isOwnProfile && (
                  <TabsContent value="private" className="mt-0 h-full">
                    <ModelGrid
                      models={privateModels}
                      showAuthor={false}
                      showVisibility={true}
                      emptyMessage="You don't have any private models."
                    />
                  </TabsContent>
                )}
              </>
            )}
          </div>
        </Tabs>
      </div>
    </div>
  );
}
